import { Clock, MapPin, CalendarClock } from "lucide-react";

import type { RequestMetadata } from "@/lib/requests-api";
import { formatTimeIn, formatDateIn } from "@/lib/datetime";

/**
 * Structured detail from the mobile app's request `metadata` blob.
 *
 * Everything here is **client-supplied and advisory** — the approver still
 * decides, and nothing is auto-applied. It is also unvalidated beyond "is an
 * object", so every field is treated as possibly-absent: a section renders
 * only when it has something real to show, and a partially-filled section
 * shows the parts it has rather than "undefined".
 *
 * Contract: `docs/mobile/requests.md` → "Request metadata".
 */

/** "2h 30m" / "45m" — null when the input isn't a usable number. */
function formatDuration(minutes: unknown): string | null {
  if (typeof minutes !== "number" || !Number.isFinite(minutes) || minutes < 0) return null;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

const text = (v: unknown): string | null =>
  typeof v === "string" && v.trim() !== "" ? v.trim() : null;

/** The fields this panel can actually render, per section. */
const RENDERABLE: Record<string, string[]> = {
  overtime: ["startTime", "endTime", "durationMinutes"],
  correctedSite: ["siteName"],
  recordedState: ["date", "firstCheckInAt", "lastCheckOutAt", "siteName", "totalWorkedMinutes"],
};

/**
 * Whether `<RequestMetadataPanel>` would render anything — so the caller knows
 * to keep showing the prose fallback for rows with no (or empty) metadata.
 * Checks the same fields the panel renders, so the two can't disagree about
 * "has detail" and leave the approver with an empty box.
 */
export function hasStructuredDetail(metadata: RequestMetadata | null | undefined): boolean {
  if (!metadata || typeof metadata !== "object") return false;
  return Object.entries(RENDERABLE).some(([section, fields]) => {
    const value = metadata[section];
    if (!value || typeof value !== "object") return false;
    const obj = value as Record<string, unknown>;
    return fields.some((f) =>
      typeof obj[f] === "number" ? Number.isFinite(obj[f] as number) : text(obj[f]) !== null,
    );
  });
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium tabular-nums">{value}</span>
    </div>
  );
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border bg-secondary/20 p-3">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" /> {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

export function RequestMetadataPanel({
  metadata,
  tz,
}: {
  metadata: RequestMetadata | null | undefined;
  tz: string;
}) {
  if (!metadata || typeof metadata !== "object") return null;

  const sections: React.ReactNode[] = [];

  // ---- OVERTIME: the clock times worked ----
  const ot = metadata.overtime;
  if (ot && typeof ot === "object") {
    const start = text(ot.startTime);
    const end = text(ot.endTime);
    const duration = formatDuration(ot.durationMinutes);
    const rows: React.ReactNode[] = [];
    if (start && end) {
      rows.push(
        <Row
          key="window"
          label="Worked"
          value={`${start} – ${end}${ot.endsNextDay === true ? " (+1 day)" : ""}`}
        />,
      );
    } else if (start) {
      rows.push(<Row key="from" label="From" value={start} />);
    }
    if (duration) rows.push(<Row key="dur" label="Duration" value={duration} />);
    if (rows.length > 0) {
      sections.push(
        <Section key="ot" icon={Clock} title="Overtime claimed">
          {rows}
        </Section>,
      );
    }
  }

  // ---- INCORRECT_LOCATION: the site it should have been ----
  const site = metadata.correctedSite;
  if (site && typeof site === "object") {
    const name = text(site.siteName);
    if (name) {
      sections.push(
        <Section key="site" icon={MapPin} title="Should have been">
          <Row label="Site" value={name} />
        </Section>,
      );
    }
  }

  // ---- Corrections: snapshot of the record at submit time ----
  const rec = metadata.recordedState;
  if (rec && typeof rec === "object") {
    const rows: React.ReactNode[] = [];
    const date = text(rec.date);
    const checkIn = text(rec.firstCheckInAt);
    const checkOut = text(rec.lastCheckOutAt);
    const siteName = text(rec.siteName);
    const worked = formatDuration(rec.totalWorkedMinutes);

    if (date) rows.push(<Row key="d" label="Date" value={formatDateIn(date, tz)} />);
    if (checkIn) rows.push(<Row key="in" label="Check-in" value={formatTimeIn(checkIn, tz)} />);
    if (checkOut) rows.push(<Row key="out" label="Check-out" value={formatTimeIn(checkOut, tz)} />);
    if (worked) rows.push(<Row key="w" label="Worked" value={worked} />);
    if (siteName) rows.push(<Row key="s" label="Site" value={siteName} />);

    if (rows.length > 0) {
      sections.push(
        <Section key="rec" icon={CalendarClock} title="Recorded at submit time">
          {rows}
        </Section>,
      );
    }
  }

  if (sections.length === 0) return null;

  return (
    <div className="space-y-2">
      {sections}
      <div className="text-[10px] text-muted-foreground">
        Submitted by the employee's app — for reference only. Approving does not apply these
        values automatically.
      </div>
    </div>
  );
}
